'use client';

import { FC, useCallback, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import WaveGridBackground from '@/components/WaveGridBackground';
import { ArrowRightIcon } from '@/assets';

// Renders a rich-text paragraph string, splitting on [BULLETPOINT] markers.
// Text before the first marker becomes a regular paragraph; each marker
// introduces a new list item.
const renderDescriptionWithBulletpoints = (text: string) => {
  if (!text.includes('[BULLETPOINT]')) {
    return <p className="mb-0">{text}</p>;
  }
  const parts = text.split('[BULLETPOINT]');
  const intro = parts[0].trim();
  const items = parts.slice(1).filter((p) => p.trim());
  return (
    <>
      {intro && <p className="mb-2">{intro}</p>}
      {items.length > 0 && (
        <ul className="list-disc space-y-1 pl-[20px]">
          {items.map((item, i) => (
            <li key={i}>{item.trim()}</li>
          ))}
        </ul>
      )}
    </>
  );
};

/**
 * Props for `FeatureComparisonGrid`.
 */
export type FeatureComparisonGridProps =
  SliceComponentProps<Content.FeatureComparisonGridSlice>;

/**
 * Component for "FeatureComparisonGrid" Slices.
 */
const FeatureComparisonGrid: FC<FeatureComparisonGridProps> = ({ slice }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const mousePos = useRef({ x: 0, y: 0 });

  // Detect which card the cursor is over (works during scroll too)
  const updateHoveredCard = useCallback(() => {
    const el = document.elementFromPoint(
      mousePos.current.x,
      mousePos.current.y,
    );
    cardsRef.current.forEach((card) => {
      if (!card) {
        return;
      }
      if (el && card.contains(el)) {
        card.classList.add('is-hovered');
      } else {
        card.classList.remove('is-hovered');
      }
    });
  }, []);

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      updateHoveredCard();
    };
    const onScroll = () => updateHoveredCard();

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true,
    });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [updateHoveredCard]);

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReduced) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: headerRef, delay: '0ms' },
      { ref: dividerRef, delay: '200ms' },
      { ref: titleRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    // Each card fades in independently as it scrolls into view
    cardsRef.current.forEach((card) => {
      if (card) {
        card.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        observer.observe(card);
      }
    });

    return () => observer.disconnect();
  }, []);

  // ── DARK EXECUTION VARIATION ────────────────────────────────────────────
  // Cast to a wider type until slicemachine push regenerates prismicio-types.d.ts
  if ((slice.variation as string) === 'darkExecution') {
    const darkSlice = slice as unknown as {
      slice_type: string;
      variation: string;
      primary: {
        section_preheading?: import('@prismicio/client').RichTextField;
        section_subtext?: import('@prismicio/client').RichTextField;
        section_description?: string;
        bullets?: { text?: string }[];
        section_button?: import('@prismicio/client').LinkField;
        feature_cards?: {
          card_category?: string;
          card_headline?: import('@prismicio/client').RichTextField;
          card_description?: import('@prismicio/client').RichTextField;
        }[];
      };
    };
    const {
      section_preheading,
      section_subtext,
      section_description,
      bullets,
      section_button,
      feature_cards,
    } = darkSlice.primary;

    return (
      <section
        data-slice-type={darkSlice.slice_type}
        data-slice-variation={darkSlice.variation}
        className="relative bg-[#01060a] px-4 py-[80px] md:px-8 lg:px-[80px]"
      >
        <div className="mx-auto flex w-full max-w-[1140px] flex-col gap-[60px]">
          {/* Eyebrow pill + divider line */}
          <div ref={dividerRef} className="flex items-center gap-[40px]">
            {isFilled.richText(section_preheading) && (
              <div className="inline-flex shrink-0 rounded-full border border-[#00f557] px-[16px] py-[8px]">
                <PrismicRichText
                  field={section_preheading}
                  components={{
                    heading6: ({ children }) => (
                      <h6 className="whitespace-nowrap text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-white md:text-[13px]">
                        {children}
                      </h6>
                    ),
                  }}
                />
              </div>
            )}
            <div className="h-px flex-1 bg-white/15" />
          </div>

          {/* Two-column layout */}
          <div className="flex flex-col gap-[40px] lg:flex-row lg:items-start lg:gap-[80px]">
            {/* Left: title + tagline + bullets + cta */}
            <div
              ref={titleRef}
              className="flex flex-col gap-[24px] lg:sticky lg:top-[120px] lg:w-[460px] lg:shrink-0"
            >
              {isFilled.richText(section_subtext) && (
                <PrismicRichText
                  field={section_subtext}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[32px] font-semibold capitalize leading-[1.4] text-white lg:text-[42px]">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                  }}
                />
              )}

              <div className="flex flex-col gap-[8px]">
                {section_description && (
                  <p className="text-[20px] font-semibold leading-[1.4] text-[#00f557]">
                    {section_description}
                  </p>
                )}

                {bullets && bullets.length > 0 && (
                  <ul className="ml-[24px] flex list-disc flex-col gap-0 text-[16px] font-light leading-[1.5] text-white">
                    {bullets.map((b, i) =>
                      b.text ? <li key={i}>{b.text}</li> : null,
                    )}
                  </ul>
                )}
              </div>

              {isFilled.link(section_button) && (
                <PrismicNextLink
                  field={section_button}
                  className="group mt-[8px] inline-flex w-fit items-center justify-center whitespace-nowrap rounded-full bg-[#00f557] px-[24px] py-[12px] font-manrope text-[16px] font-bold leading-[1.5] text-[#002726] transition-all duration-300 from-[#00F2A5] to-[#00C2FF] hover:bg-gradient-to-r hover:shadow-[0_0_15px_rgba(0,242,165,0.8)] hover:text-white"
                >
                  <ArrowRightIcon className="mr-0 w-0 opacity-0 transition-all duration-300 group-hover:mr-2 group-hover:w-5 group-hover:opacity-100" />
                  {(section_button as { text?: string }).text}
                </PrismicNextLink>
              )}
            </div>

            {/* Right: stacked feature cards */}
            {feature_cards && feature_cards.length > 0 && (
              <div className="flex flex-1 flex-col gap-[14px] lg:w-[420px] lg:max-w-[420px] lg:flex-none">
                {feature_cards.map((card, index) => (
                  <div
                    key={index}
                    ref={(el) => {
                      cardsRef.current[index] = el;
                    }}
                    className="flex min-h-[150px] flex-col justify-center gap-[14px] rounded-[20px] border border-white/15 bg-[#0a0f1c]/40 p-[24px] md:p-[28px]"
                  >
                    {card.card_category && (
                      <div className="flex items-center gap-[10px]">
                        <span className="block size-[10px] shrink-0 rounded-full border-2 border-[#00f557]" />
                        <span className="text-[12px] font-normal uppercase leading-[1.2] tracking-[0.06em] text-[#00f557]">
                          {card.card_category}
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col gap-[6px]">
                      {isFilled.richText(card.card_headline) && (
                        <PrismicRichText
                          field={card.card_headline}
                          components={{
                            heading5: ({ children }) => (
                              <h5 className="text-[18px] font-bold leading-[1.25] text-white md:text-[20px]">
                                {children}
                              </h5>
                            ),
                          }}
                        />
                      )}

                      {isFilled.richText(card.card_description) && (
                        <PrismicRichText
                          field={card.card_description}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="text-[13px] font-light leading-[1.5] text-white/75 md:text-[14px]">
                                {children}
                              </p>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-semibold">
                                {children}
                              </strong>
                            ),
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // ── GARTNER RECOGNITION VARIATION ───────────────────────────────────────
  if (slice.variation === 'gartnerRecognition') {
    const {
      section_headline,
      gartner_logo,
      section_description,
      feature_cards,
      logo_strip,
    } = slice.primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative mx-[30px] flex flex-col items-center gap-[40px] bg-[#FAFAFA] px-4 pb-[80px] pt-[80px] md:px-8 lg:px-[150px]"
      >
        <WaveGridBackground />

        {/* Header — centered headline + Gartner logo + description */}
        <div
          ref={headerRef}
          className="relative z-[1] mx-auto flex w-full max-w-[1080px] flex-col items-center gap-[16px] text-center"
        >
          {isFilled.richText(section_headline) && (
            <PrismicRichText
              field={section_headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="w-full break-words text-[26px] font-semibold leading-[1.2] text-[#041118] sm:text-[32px] lg:text-[42px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.image(gartner_logo) && (
            <PrismicNextImage
              field={gartner_logo}
              className="h-[81px] w-auto object-contain"
              fallbackAlt=""
            />
          )}

          {section_description && (
            <p className="max-w-[820px] text-[16px] font-light leading-[1.5] text-[#041118]">
              {section_description}
            </p>
          )}
        </div>

        {/* Two-card row */}
        {feature_cards.length > 0 && (
          <div className="relative z-[1] mx-auto grid w-full max-w-[1080px] grid-cols-1 gap-[24px] md:grid-cols-2">
            {feature_cards.map((card, index) => (
              <div
                key={index}
                ref={(el) => {
                  cardsRef.current[index] = el;
                }}
                className="group relative flex flex-col gap-[24px] overflow-hidden rounded-[24px] border border-[#ccc] bg-white p-[36px] transition-all duration-500"
              >
                {isFilled.image(card.card_image) && (
                  <div className="relative h-[180px] w-full overflow-hidden rounded-[16px]">
                    <PrismicNextImage
                      field={card.card_image}
                      fill
                      className="object-cover"
                      fallbackAlt=""
                    />
                  </div>
                )}

                {isFilled.richText(card.card_headline) && (
                  <PrismicRichText
                    field={card.card_headline}
                    components={{
                      heading5: ({ children }) => (
                        <h5 className="text-[22px] font-semibold leading-[1.3] text-[#041118]">
                          {children}
                        </h5>
                      ),
                    }}
                  />
                )}

                {isFilled.richText(card.card_description) && (
                  <PrismicRichText
                    field={card.card_description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-[16px] font-light leading-[1.5] text-[#041118]">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold">{children}</strong>
                      ),
                    }}
                  />
                )}

                {isFilled.link(card.card_button) && (
                  <PrismicNextLink
                    field={card.card_button}
                    className="mt-auto inline-flex w-fit items-center gap-[8px] rounded-full border border-[#041118] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#041118] transition-colors hover:bg-[#041118] hover:text-white"
                  >
                    {card.card_button.text && (
                      <span>{card.card_button.text}</span>
                    )}
                    <span>→</span>
                  </PrismicNextLink>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Logo strip */}
        {logo_strip.length > 0 && (
          <div className="relative z-[1] mx-auto flex w-full max-w-[1080px] flex-wrap items-center justify-center gap-x-[40px] gap-y-[24px]">
            {logo_strip.map((item, index) => (
              <div
                key={index}
                className="flex h-[70px] items-center justify-center"
              >
                {isFilled.image(item.logo) && (
                  <PrismicNextImage
                    field={item.logo}
                    className="h-auto max-h-[70px] w-auto object-contain grayscale transition-all duration-300 hover:grayscale-0"
                    fallbackAlt=""
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  // ── CARDLESS AND SIMPLER VARIATION ──────────────────────────────────────
  if (slice.variation === 'cardlessAndSimpler') {
    const {
      section_preheading,
      section_subtext,
      section_description,
      feature_cards,
    } = slice.primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative mx-[30px] flex flex-col items-start gap-[40px] bg-[#FAFAFA] px-4 pb-[80px] md:px-8 lg:px-[150px]"
      >
        <WaveGridBackground />

        <div className="relative z-[1] flex w-full flex-col gap-[60px] pt-[80px]">
          {/* Divider line with label pill */}
          <div ref={dividerRef} className="flex items-center gap-[60px]">
            {isFilled.richText(section_preheading) && (
              <div className="inline-flex shrink-0 rounded-full border border-[#4b0fea] bg-[#FAFAFA] px-[16px] py-[8px]">
                <PrismicRichText
                  field={section_preheading}
                  components={{
                    heading6: ({ children }) => (
                      <h6 className="whitespace-nowrap text-[14px] font-normal uppercase leading-[1.2] text-[#041118]">
                        {children}
                      </h6>
                    ),
                  }}
                />
              </div>
            )}
            <div className="h-px flex-1 bg-[#ccc]" />
          </div>

          {/* Heading + Items layout */}
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-[80px]">
            {/* Left — sticky heading + description */}
            <div
              ref={titleRef}
              className="flex flex-col gap-[24px] lg:sticky lg:top-[200px] lg:w-[540px] lg:shrink-0"
            >
              {isFilled.richText(section_subtext) && (
                <PrismicRichText
                  field={section_subtext}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[32px] font-semibold capitalize leading-[1.4] text-[#041118] lg:text-[42px]">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                  }}
                />
              )}
              {section_description && (
                <p className="text-[16px] font-light leading-[1.5] text-[#041118]">
                  {section_description}
                </p>
              )}
            </div>

            {/* Right — items without card borders */}
            <div className="flex w-full flex-col lg:w-[460px] lg:shrink-0">
              {feature_cards.map((card, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    cardsRef.current[index] = el;
                  }}
                  className="flex flex-col gap-[16px] pb-[60px]"
                >
                  {/* Large number */}
                  <span
                    className="select-none font-bold leading-none text-[#E8E8E8]"
                    style={{ fontSize: '90px' }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Headline */}
                  {isFilled.richText(card.card_headline) && (
                    <PrismicRichText
                      field={card.card_headline}
                      components={{
                        heading5: ({ children }) => (
                          <h5 className="text-[22px] font-semibold leading-[1.5] text-[#4b0fea]">
                            {children}
                          </h5>
                        ),
                      }}
                    />
                  )}

                  {/* Description */}
                  {isFilled.richText(card.card_description) && (
                    <div className="text-[16px] font-light leading-[1.5] text-[#041118]">
                      <PrismicRichText
                        field={card.card_description}
                        components={{
                          paragraph: ({ children }) => <p>{children}</p>,
                          strong: ({ children }) => (
                            <strong className="font-semibold">
                              {children}
                            </strong>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {/* Button */}
                  {isFilled.link(card.card_button) && (
                    <PrismicNextLink
                      field={card.card_button}
                      className="group inline-flex w-fit items-center rounded-full border border-[#041118] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#041118] transition-all duration-500 hover:border-transparent hover:text-white hover:[background:radial-gradient(55.37%_55.22%_at_50.29%_89.58%,#05f273_0%,#03bf9e_100%)] hover:[box-shadow:0px_0px_0px_3px_rgba(3,194,155,0.2),0px_0px_60px_0px_#05f36c]"
                    >
                      <ArrowRightIcon className="w-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:mr-2 group-hover:w-5 group-hover:opacity-100" />
                      {card.card_button.text && (
                        <span>{card.card_button.text}</span>
                      )}
                    </PrismicNextLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── CARDLESS VARIATION ────────────────────────────────────────────────────
  if (slice.variation === 'cardless') {
    const { section_preheading, section_subtext, feature_cards } =
      slice.primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative mx-[30px] flex flex-col items-start gap-[40px] bg-[#FAFAFA] px-4 pb-[80px] md:px-8 lg:px-[150px]"
      >
        <WaveGridBackground />

        <div className="relative z-[1] flex w-full flex-col gap-[40px] pt-[80px]">
          {/* Divider line with label pill */}
          <div ref={dividerRef} className="flex items-center gap-[60px]">
            {isFilled.richText(section_preheading) && (
              <div className="inline-flex shrink-0 rounded-full border border-[#4b0fea] bg-[#FAFAFA] px-[16px] py-[8px]">
                <PrismicRichText
                  field={section_preheading}
                  components={{
                    heading6: ({ children }) => (
                      <h6 className="whitespace-nowrap text-[14px] font-normal uppercase leading-[1.2] text-[#041118]">
                        {children}
                      </h6>
                    ),
                  }}
                />
              </div>
            )}
            <div className="h-px flex-1 bg-[#ccc]" />
          </div>

          {/* Heading + Items layout */}
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-[80px]">
            {/* Left — sticky heading (parallax) */}
            <div
              ref={titleRef}
              className="lg:sticky lg:top-[200px] lg:w-[540px] lg:shrink-0"
            >
              {isFilled.richText(section_subtext) && (
                <PrismicRichText
                  field={section_subtext}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[32px] font-semibold leading-[1.2] text-[#041118] lg:text-[42px]">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                  }}
                />
              )}
            </div>

            {/* Right — items without card borders */}
            <div className="flex w-full flex-col lg:w-[460px] lg:shrink-0">
              {feature_cards.map((card, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    cardsRef.current[index] = el;
                  }}
                  className="flex flex-col gap-[16px] pb-[60px]"
                >
                  {/* Large number — programmatic */}
                  <span
                    className="select-none font-bold leading-none text-[#E8E8E8]"
                    style={{ fontSize: '90px' }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Category */}
                  {card.card_category && (
                    <p className="text-[14px] font-normal uppercase leading-[1.5] text-[#4b0fea]">
                      {card.card_category}
                    </p>
                  )}

                  {/* Headline */}
                  {isFilled.richText(card.card_headline) && (
                    <PrismicRichText
                      field={card.card_headline}
                      components={{
                        heading5: ({ children }) => (
                          <h5 className="text-[22px] font-semibold leading-[1.3] text-[#041118]">
                            {children}
                          </h5>
                        ),
                      }}
                    />
                  )}

                  {/* Description */}
                  {isFilled.richText(card.card_description) && (
                    <div className="text-[16px] font-light leading-[1.5] text-[#041118]">
                      <PrismicRichText
                        field={card.card_description}
                        components={{
                          paragraph: ({ node }) => {
                            const raw = node.text ?? '';
                            return renderDescriptionWithBulletpoints(raw);
                          },
                          strong: ({ children }) => (
                            <strong className="font-semibold">
                              {children}
                            </strong>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {/* The Result — hardcoded label, text from prismic */}
                  {card.card_highlight && (
                    <div className="flex items-start gap-[8px]">
                      <svg
                        className="mt-[4px] size-[32px] shrink-0"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <circle
                          cx="16"
                          cy="16"
                          r="14.5"
                          stroke="#4b0fea"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M10 16.5L14 20.5L22 12.5"
                          stroke="#4b0fea"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="flex-1 text-[20px] leading-[1.5] text-[#041118]">
                        <span className="font-semibold text-[#4b0fea]">
                          The Result:{' '}
                        </span>
                        <span className="font-light">
                          {card.card_highlight}
                        </span>
                      </p>
                    </div>
                  )}

                  {/* Button */}
                  {isFilled.link(card.card_button) && (
                    <PrismicNextLink
                      field={card.card_button}
                      className="inline-flex w-fit items-center gap-[8px] rounded-full border border-[#041118] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#041118] transition-all duration-500 hover:border-t-white hover:border-transparent hover:text-white hover:[background:radial-gradient(55.37%_55.22%_at_50.29%_89.58%,#05f273_0%,#03bf9e_100%)] hover:[box-shadow:0px_0px_0px_3px_rgba(3,194,155,0.2),0px_0px_60px_0px_#05f36c]"
                    >
                      {card.card_button.text && (
                        <span>{card.card_button.text}</span>
                      )}
                      <span>→</span>
                    </PrismicNextLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── DEFAULT: MAIN WITH INTRO HEADER ──────────────────────────────────────
  const mainPrimary = slice.primary as unknown as {
    gartner_logo?: import('@prismicio/client').ImageField;
    section_headline?: import('@prismicio/client').RichTextField;
    section_button?: import('@prismicio/client').LinkField;
    section_preheading?: import('@prismicio/client').RichTextField;
    section_subtext?: import('@prismicio/client').RichTextField;
    feature_cards?: {
      card_headline?: import('@prismicio/client').RichTextField;
      card_category?: string;
      card_description?: import('@prismicio/client').RichTextField;
      card_highlight?: string;
      card_button?: import('@prismicio/client').LinkField;
    }[];
    cards_shared_on_hover_background?: import('@prismicio/client').ImageField;
    section_description?: string;
  };
  const {
    gartner_logo,
    section_headline,
    section_button,
    section_preheading,
    section_subtext,
    feature_cards,
    cards_shared_on_hover_background,
    section_description,
  } = mainPrimary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-[30px] flex flex-col items-start gap-[40px] bg-[#FAFAFA] px-4 pb-[80px] md:px-8 lg:px-[150px]"
    >
      <WaveGridBackground />

      {/* Gartner CTA Header */}
      <div
        ref={headerRef}
        className="relative z-[1] flex w-full flex-col items-center gap-[24px] pb-[60px] pt-[80px]"
      >
        {isFilled.image(gartner_logo) && (
          <PrismicNextImage
            field={gartner_logo}
            className="h-[81px] w-auto object-contain"
            fallbackAlt=""
          />
        )}

        {isFilled.richText(section_headline) && (
          <PrismicRichText
            field={section_headline}
            components={{
              heading2: ({ children }) => (
                <h2 className="max-w-[698px] text-center text-[20px] font-medium leading-[1.4] text-[#041118] md:text-[24px]">
                  {children}
                </h2>
              ),
            }}
          />
        )}

        {isFilled.link(section_button) && (
          <PrismicNextLink
            field={section_button}
            className="rounded-full border border-[#041118] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#041118] transition-colors hover:bg-[#041118] hover:text-white"
          />
        )}
      </div>

      {/* Divider + Heading + Cards — shared container so line aligns with card edge */}
      <div className="relative z-[1] mx-auto flex w-full max-w-[1080px] flex-col gap-[40px]">
        {/* Divider line with label pill */}
        <div ref={dividerRef} className="flex items-center gap-[60px]">
          {isFilled.richText(section_preheading) && (
            <div className="inline-flex shrink-0 rounded-full border border-[#4b0fea] bg-[#FAFAFA] px-[16px] py-[8px]">
              <PrismicRichText
                field={section_preheading}
                components={{
                  heading6: ({ children }) => (
                    <h6 className="whitespace-nowrap text-[14px] font-semibold uppercase leading-[1.2] text-[#041118]">
                      {children}
                    </h6>
                  ),
                }}
              />
            </div>
          )}
          <div className="h-px flex-1 bg-[#ccc]" />
        </div>

        {/* Heading + Cards Layout */}
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-[80px]">
          {/* Left Column — Heading (sticky parallax on desktop) */}
          <div
            ref={titleRef}
            className="flex flex-col gap-[24px] lg:sticky lg:top-[200px] lg:w-[540px] lg:shrink-0"
          >
            {isFilled.richText(section_subtext) && (
              <PrismicRichText
                field={section_subtext}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-[32px] font-semibold leading-[1.4] text-[#041118] lg:text-[42px]">
                      {children}
                    </p>
                  ),
                }}
              />
            )}
            {section_description && (
              <p className="text-[16px] font-light leading-[1.5] text-[#041118]">
                {section_description}
              </p>
            )}
          </div>

          {/* Right Column — Feature Cards */}
          <div className="flex w-full flex-col gap-[24px] lg:w-[460px] lg:shrink-0">
            {(feature_cards ?? []).map((card, index) => (
              <div
                key={index}
                ref={(el) => {
                  cardsRef.current[index] = el;
                }}
                className="group relative flex flex-col gap-[24px] rounded-[24px] border border-[#ccc] p-[36px] backdrop-blur-[21px] overflow-hidden transition-all duration-500 [&.is-hovered]:border-transparent"
              >
                {/* Hover background image */}
                {isFilled.image(cards_shared_on_hover_background) && (
                  <div className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-500 group-[.is-hovered]:opacity-100">
                    <PrismicNextImage
                      field={cards_shared_on_hover_background}
                      className="h-full w-full object-cover"
                      fill
                      fallbackAlt=""
                    />
                    {/* Dark overlay for readability */}
                    <div className="absolute inset-0 bg-black/75" />
                  </div>
                )}

                {/* Card Category */}
                {card.card_category && (
                  <div className="relative z-[1] flex items-center gap-[16px]">
                    <span className="block size-[12px] shrink-0 rounded-full border-2 border-[#4b0fea] transition-colors duration-500 group-[.is-hovered]:border-[#00f557]" />
                    <span className="text-[14px] font-normal uppercase leading-[1.2] text-[#4b0fea] transition-colors duration-500 group-[.is-hovered]:text-[#00f557]">
                      {card.card_category}
                    </span>
                  </div>
                )}

                {/* Card Body */}
                <div className="relative z-[1] flex flex-col gap-[8px]">
                  {isFilled.richText(card.card_headline) && (
                    <PrismicRichText
                      field={card.card_headline}
                      components={{
                        heading5: ({ children }) => (
                          <h5 className="text-[24px] font-semibold leading-[1.2] text-[#041118] transition-colors duration-500 group-[.is-hovered]:text-white">
                            {children}
                          </h5>
                        ),
                      }}
                    />
                  )}

                  {isFilled.richText(card.card_description) && (
                    <PrismicRichText
                      field={card.card_description}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="text-[16px] font-light leading-[1.5] text-[#041118] transition-colors duration-500 group-[.is-hovered]:text-white">
                            {children}
                          </p>
                        ),
                      }}
                    />
                  )}

                  {card.card_highlight && (
                    <p className="text-[18px] font-medium leading-[1.5] text-[#4b0fea] transition-colors duration-500 group-[.is-hovered]:text-[#00f557]">
                      {card.card_highlight}
                    </p>
                  )}
                </div>

                {/* Card Button — arrow flips from start→end on card hover, gradient on button hover */}
                {isFilled.link(card.card_button) && (
                  <PrismicNextLink
                    field={card.card_button}
                    className="relative z-[1] inline-flex w-fit items-center gap-[8px] rounded-full border border-[#041118] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#041118] transition-all duration-500 group-[.is-hovered]:border-white group-[.is-hovered]:text-white hover:border-t-white hover:border-transparent hover:text-white hover:[background:radial-gradient(55.37%_55.22%_at_50.29%_89.58%,#05f273_0%,#03bf9e_100%)] hover:[box-shadow:0px_0px_0px_3px_rgba(3,194,155,0.2),0px_0px_60px_0px_#05f36c]"
                  >
                    <span className="transition-all duration-500 group-[.is-hovered]:order-2">
                      →
                    </span>
                    {card.card_button.text && (
                      <span className="transition-all duration-500 group-[.is-hovered]:order-1">
                        {card.card_button.text}
                      </span>
                    )}
                  </PrismicNextLink>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureComparisonGrid;
